/**
 * Parts mounted on a tube (D40, tubing sets): a pinch valve, a clamp-on
 * sensor or a manual clamp sits on an unbroken tube instead of cutting it in
 * two.
 *
 * Two positions, kept apart as the invariants ask:
 *
 * - `distance`, hydraulic, metres from the tube's `from_port` end. Only ever
 *   set on purpose, in the inspector. The inspector shows it from the left end
 *   of a mostly horizontal tube or the top of a mostly vertical one, and
 *   converts, so redrawing the tube never moves the part (invariant 4).
 * - `along`, where the symbol is drawn, as a fraction of the drawn line. Set
 *   by dragging, and by dropping the part on the line.
 */
import type { DesignDocument, EdgeDocument, NodeDocument, TemplateDocument } from "../design/document";
import { buildScene, DEFAULT_VIEW, formatLength } from "../scene";
import { fractionNearest } from "../scene/build";
import type { Point, Scene, SceneEdge, SceneLibrary } from "../scene/types";

/** The ways a part sits on a tube: an instrument's (`clamp_on`), or the set's own (`on_tube`). */
const MOUNTINGS = new Set(["clamp_on", "on_tube"]);

export function mountingOf(node: NodeDocument, template: TemplateDocument | undefined): string | undefined {
  const value =
    node.parameters?.mounting ?? template?.parameters?.find((parameter) => parameter.key === "mounting")?.default;
  return typeof value === "string" ? value : undefined;
}

/** Whether a part can be mounted on a tube: it sits on one, and a tube can run through it. */
export function isMountable(node: NodeDocument, template: TemplateDocument | undefined): boolean {
  const mounting = mountingOf(node, template);
  return mounting !== undefined && MOUNTINGS.has(mounting) && throughPorts(node).length === 2;
}

/** The fluidic ports a tube runs through, upstream first (as the server reads them). */
export function throughPorts(node: NodeDocument): string[] {
  return (node.ports ?? []).filter((port) => (port.kind ?? "fluidic") === "fluidic").map((port) => port.local_id);
}

const scenes = new WeakMap<DesignDocument, WeakMap<SceneLibrary, Scene>>();

/** The design as drawn, with every block expanded: where lines run. Cached per document. */
export function drawnScene(design: DesignDocument, library: SceneLibrary): Scene {
  let byLibrary = scenes.get(design);
  if (!byLibrary) {
    byLibrary = new WeakMap();
    scenes.set(design, byLibrary);
  }
  let scene = byLibrary.get(library);
  if (!scene) {
    scene = buildScene(design, library, [], DEFAULT_VIEW);
    byLibrary.set(library, scene);
  }
  return scene;
}

/**
 * Mount a just-placed part on a line where it was dropped. Its hydraulic
 * distance is the middle of the tube, and the notice says so: where a symbol
 * was dropped on a drawing says nothing about where it is on the tube.
 */
export function mountOnDrop(
  design: DesignDocument,
  library: SceneLibrary,
  node: NodeDocument,
  edgeRef: string,
  at: Point,
): { document: DesignDocument; message: string } | null {
  const edge = design.edges?.find((candidate) => candidate.local_id === edgeRef);
  const drawn = drawnScene(design, library).edges.find((candidate) => candidate.ref === edgeRef);
  if (!edge || !drawn) return null;
  const length = edge.hydraulic?.length ?? null;
  const distance = length !== null ? length / 2 : 0;
  const mounted: NodeDocument = { ...node, mount: { edge: edgeRef, distance, along: fractionNearest(drawn.path, at) } };
  return {
    document: { ...design, nodes: (design.nodes ?? []).map((candidate) => (candidate === node ? mounted : candidate)) },
    message:
      length !== null
        ? `Mounted ${node.local_id} on ${edgeRef}, ${formatLength(distance)} along it: the middle. Set where it sits in the inspector.`
        : `Mounted ${node.local_id} on ${edgeRef}. ${edgeRef} has no length yet; set it, then where ${node.local_id} sits.`,
  };
}

/** Slide mounted parts along their lines as the pointer moves them: presentation only. */
export function slideMounted(
  base: DesignDocument,
  library: SceneLibrary,
  ids: ReadonlySet<string>,
  dx: number,
  dy: number,
): DesignDocument {
  const scene = drawnScene(base, library);
  let changed = false;
  const nodes = (base.nodes ?? []).map((node) => {
    if (!node.mount || !ids.has(node.local_id)) return node;
    const drawnNode = scene.nodes.find((candidate) => candidate.ref === node.local_id);
    const line = scene.edges.find((candidate) => candidate.ref === node.mount!.edge);
    if (!drawnNode || !line) return node;
    const along = fractionNearest(line.path, { x: drawnNode.centre.x + dx, y: drawnNode.centre.y + dy });
    if (node.mount.along === along) return node;
    changed = true;
    return { ...node, mount: { ...node.mount, along } };
  });
  return changed ? { ...base, nodes } : base;
}

/**
 * Parts on lines that are going: they stay, in line with nothing, where they
 * were drawn. Deleting a tube never deletes what a designer put on it.
 */
export function unmountFrom(design: DesignDocument, library: SceneLibrary, removed: ReadonlySet<string>, nodes: NodeDocument[]): NodeDocument[] {
  const scene = drawnScene(design, library);
  return nodes.map((node) => {
    if (!node.mount || !removed.has(node.mount.edge)) return node;
    const drawnNode = scene.nodes.find((candidate) => candidate.ref === node.local_id);
    const { mount: _mount, ...rest } = node;
    void _mount;
    return drawnNode
      ? {
          ...rest,
          schematic_position: {
            ...(node.schematic_position ?? {}),
            x: drawnNode.centre.x,
            y: drawnNode.centre.y,
            rotation: drawnNode.rotation,
            mirrored: drawnNode.mirrored,
          },
        }
      : rest;
  });
}

/**
 * Put a part drawn in line between two tubes onto one tube (D40): the two
 * tubes become one, keeping the first one's identifier, running from its far
 * end to the second's, and the part sits where they met. Refused unless the
 * part can be mounted and has a tube on each of its two through ports.
 */
export function mountOnLine(
  design: DesignDocument,
  library: SceneLibrary,
  ref: string,
): { document: DesignDocument; message: string } | { refusal: string } {
  const node = design.nodes?.find((candidate) => candidate.local_id === ref);
  if (!node) return { refusal: `${ref} is not a component in this design` };
  if (node.mount) return { refusal: `${ref} is already mounted on ${node.mount.edge}` };
  const template = node.template_version_id ? library.templates[node.template_version_id] : undefined;
  if (!isMountable(node, template)) {
    return { refusal: `${ref} sits in the tube, not on it, so it cannot be mounted on a line` };
  }
  const [upstream, downstream] = throughPorts(node).map((port) => `${ref}.${port}`);
  const edges = design.edges ?? [];
  const on = (port: string) => edges.find((edge) => edge.from_port === port || edge.to_port === port);
  const first = on(upstream!);
  const second = on(downstream!);
  if (!first || !second) return { refusal: `${ref} needs a tube on each side to be mounted on one` };
  // Each tube's far end: the one that is not at the part.
  const farOf = (edge: EdgeDocument, near: string) => (edge.from_port === near ? edge.to_port : edge.from_port);
  const start = farOf(first, upstream!);
  const end = farOf(second, downstream!);
  const firstLength = first.hydraulic?.length ?? null;
  const secondLength = second.hydraulic?.length ?? null;
  const joined: EdgeDocument = {
    ...first,
    from_port: start,
    to_port: end,
    hydraulic: {
      ...(first.hydraulic ?? {}),
      length: firstLength !== null && secondLength !== null ? firstLength + secondLength : (first.hydraulic?.length ?? undefined),
    },
    // The joined tube is routed afresh: neither tube's bends fit it.
    schematic_waypoints: undefined,
  };
  if (joined.schematic_waypoints === undefined) delete joined.schematic_waypoints;
  const scene = drawnScene(design, library);
  const drawnFirst = scene.edges.find((edge) => edge.ref === first.local_id);
  const drawnSecond = scene.edges.find((edge) => edge.ref === second.local_id);
  const drawnLength = (edge: SceneEdge | undefined) =>
    edge ? edge.path.slice(1).reduce((sum, point, index) => sum + Math.hypot(point.x - edge.path[index]!.x, point.y - edge.path[index]!.y), 0) : 0;
  const drawnTotal = drawnLength(drawnFirst) + drawnLength(drawnSecond);
  const mounted: NodeDocument = {
    ...node,
    mount: {
      edge: first.local_id,
      distance: firstLength ?? 0,
      along: drawnTotal > 0 ? drawnLength(drawnFirst) / drawnTotal : 0.5,
    },
  };
  // Other parts already mounted on the second tube move onto the joined one, further along.
  const nodes = (design.nodes ?? []).map((candidate) => {
    if (candidate === node) return mounted;
    if (candidate.mount?.edge === second.local_id) {
      return { ...candidate, mount: { ...candidate.mount, edge: first.local_id, distance: (firstLength ?? 0) + candidate.mount.distance, along: null } };
    }
    if (candidate.mount?.edge === first.local_id) return { ...candidate, mount: { ...candidate.mount, along: null } };
    return candidate;
  });
  const lengthNote =
    firstLength !== null && secondLength !== null
      ? `${first.local_id} is now ${formatLength(firstLength + secondLength)}, and ${ref} sits ${formatLength(firstLength)} along it`
      : `set ${first.local_id}'s length, then where ${ref} sits`;
  return {
    document: {
      ...design,
      nodes,
      edges: edges.filter((edge) => edge !== second).map((edge) => (edge === first ? joined : edge)),
    },
    message: `Mounted ${ref} on ${first.local_id}, which now runs on through it in place of ${second.local_id}: ${lengthNote}.`,
  };
}

/**
 * Which end of a drawn tube the inspector counts from: the left end of a
 * mostly horizontal tube, the top of a mostly vertical one. True when that is
 * the tube's own start (`from_port`), so the stored distance reads as it is.
 */
export function countsFromStart(line: SceneEdge): boolean {
  const start = line.path[0];
  const end = line.path[line.path.length - 1];
  if (!start || !end) return true;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.abs(dx) >= Math.abs(dy) ? dx >= 0 : dy >= 0;
}

/** The drawn end the inspector counts from, in words. */
export function countedEnd(line: SceneEdge): "left" | "top" {
  const start = line.path[0];
  const end = line.path[line.path.length - 1];
  if (!start || !end) return "left";
  return Math.abs(end.x - start.x) >= Math.abs(end.y - start.y) ? "left" : "top";
}

/** Stored distance (from the tube's start) as the inspector shows it (from the left or top). */
export function shownDistance(distance: number, length: number | null, fromStart: boolean): number {
  return fromStart || length === null ? distance : length - distance;
}

/** What the inspector was given, back as a distance from the tube's start. */
export function storedDistance(shown: number, length: number | null, fromStart: boolean): number {
  return fromStart || length === null ? shown : length - shown;
}
